<template>
  <b-container fluid class="analytics-root">
    <b-row>
      <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav" style="width: 100%;">
        <b-collapse is-nav>
          <b-navbar-nav>
            <b-nav-item :active="activeTab === 'overview'" @click="activeTab = 'overview'">
              overview
            </b-nav-item>
            <b-nav-item :active="activeTab === 'spreadsheets'" @click="activeTab = 'spreadsheets'">
              spreadsheets
            </b-nav-item>
            <b-nav-item :active="activeTab === 'pages'" @click="activeTab = 'pages'">
              live app
            </b-nav-item>
            <b-nav-item :active="activeTab === 'automations'" @click="activeTab = 'automations'">
              automations
            </b-nav-item>
            <b-nav-item :active="activeTab === 'workflows'" @click="activeTab = 'workflows'">
              workflows
            </b-nav-item>
            <b-nav-item :active="activeTab === 'schedules'" @click="activeTab = 'schedules'">
              schedules
            </b-nav-item>
            <b-nav-item :active="activeTab === 'hooks'" @click="activeTab = 'hooks'">
              hooks
            </b-nav-item>
            <b-nav-item :active="activeTab === 'jobs'" @click="activeTab = 'jobs'">
              jobs
            </b-nav-item>
            <b-nav-item :active="activeTab === 'api'" @click="activeTab = 'api'">
              api
            </b-nav-item>
            <b-nav-item :active="activeTab === 'audit'" @click="activeTab = 'audit'">
              audit
            </b-nav-item>
            <li class="nav-item d-flex align-items-center">
              <modal-help :topic="analyticsHelpTopic" label="Help for this screen" show-label variant="link" />
            </li>
          </b-navbar-nav>
          <b-navbar-nav class="ms-auto">
            <b-nav-item>
              <select v-model="dateRange" class="analytics-date-select" @change="onDateRangeChange">
                <option value="30m">last 30 minutes</option>
                <option value="1h">last 1 hour</option>
                <option value="8h">last 8 hours</option>
                <option value="1d">last 1 day</option>
                <option value="7d">last 7 days</option>
                <option value="30d">last 30 days</option>
                <option value="90d">last 90 days</option>
                <option value="all">all time</option>
              </select>
            </b-nav-item>
            <b-nav-item>
              <b-button size="sm" @click="refresh" :disabled="loading">
                <font-awesome-icon icon="sync" :spin="loading" /> refresh
              </b-button>
            </b-nav-item>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    </b-row>

    <div class="analytics-content">
      <AnalyticsActivity
        v-if="activityTab"
        :section="activeTab"
        :report="activity"
        :loading="activityLoading"
        :error="activityError"
        @open="activeTab = $event"
      />
      <AnalyticsSpreadsheets
        v-if="activeTab === 'spreadsheets'"
        :application="application"
        :from="fromDate"
        :to="toDate"
        :refreshKey="refreshKey"
      />
      <AnalyticsPages
        v-if="activeTab === 'pages'"
        :application="application"
        :from="fromDate"
        :to="toDate"
        :refreshKey="refreshKey"
      />
      <AnalyticsApi
        v-if="activeTab === 'api'"
        :application="application"
        :from="fromDate"
        :to="toDate"
        :refreshKey="refreshKey"
      />
      <AnalyticsAudit
        v-if="activeTab === 'audit'"
        :application="application"
        :from="fromDate"
        :to="toDate"
        :refreshKey="refreshKey"
      />
    </div>
  </b-container>
</template>

<script>

import axios from 'axios'
import AnalyticsActivity from './analytics_activity.vue'
import AnalyticsSpreadsheets from './analytics_spreadsheets.vue'
import AnalyticsPages from './analytics_pages.vue'
import AnalyticsApi from './analytics_api.vue'
import AnalyticsAudit from './analytics_audit.vue'

const ACTIVITY_TABS = ['overview', 'automations', 'workflows', 'schedules', 'hooks', 'jobs']

export default {
  name: 'Analytics',
  components: { AnalyticsActivity, AnalyticsSpreadsheets, AnalyticsPages, AnalyticsApi, AnalyticsAudit },
  data () {
    return {
      activeTab: 'overview',
      dateRange: '30d',
      loading: false,
      refreshKey: 0,
      activity: null,
      activityLoading: false,
      activityError: '',
      activityRequest: 0
    }
  },
  computed: {
    application () {
      return this.$route.params.application
    },
    activityTab () {
      return ACTIVITY_TABS.includes(this.activeTab)
    },
    analyticsHelpTopic () {

      return {
        overview: 'analytics-overview',
        spreadsheets: 'analytics-spreadsheets',
        pages: 'analytics-pages',
        automations: 'analytics-automations',
        workflows: 'analytics-workflows',
        schedules: 'analytics-schedules',
        hooks: 'analytics-hooks',
        jobs: 'analytics-jobs',
        api: 'analytics-api',
        audit: 'analytics-audit'
      }[this.activeTab] || 'analytics-overview'
    },
    fromDate () {

      if (this.dateRange === 'all') return null

      const d = new Date()
      const val = this.dateRange

      if (val === '30m') d.setMinutes(d.getMinutes() - 30)
      else if (val === '1h') d.setHours(d.getHours() - 1)
      else if (val === '8h') d.setHours(d.getHours() - 8)
      else if (val === '1d') d.setDate(d.getDate() - 1)
      else d.setDate(d.getDate() - parseInt(val))

      return d.toISOString()
    },
    toDate () {
      return new Date().toISOString()
    }
  },
  watch: {
    refreshKey () {
      this.loadActivity()
    },
    application () {
      this.activity = null
      this.loadActivity()
    },
    activeTab () {
      if (this.activityTab && !this.activity) this.loadActivity()
    }
  },
  mounted () {
    this.loadActivity()
  },
  methods: {
    async loadActivity () {

      if (!this.activityTab) return

      const request = ++this.activityRequest

      this.activityLoading = true
      this.activityError = ''

      try {

        const params = {}

        if (this.fromDate) params.from = this.fromDate
        if (this.toDate) params.to = this.toDate

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/analytics/activity`, {
          headers: { 'application-unique-title': this.application },
          params
        })

        if (request !== this.activityRequest) return

        this.activity = res.data
      } catch (error) {

        if (request !== this.activityRequest) return

        this.activityError = 'Activity could not be loaded.'
        console.log('failed to load app activity', error)
      } finally {

        if (request === this.activityRequest) this.activityLoading = false
      }
    },
    onDateRangeChange () {
      this.refreshKey++
    },
    refresh () {
      this.loading = true
      this.refreshKey++
      setTimeout(() => { this.loading = false }, 500)
    }
  }
}
</script>

<style scoped>

.analytics-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.analytics-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.analytics-date-select {
  background: transparent;
  border: 1px solid #333330;
  color: #909090;
  font-size: 0.78rem;
  padding: 3px 10px;
  border-radius: 4px;
  outline: none;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}

.analytics-date-select:hover {
  background-color: #222220;
  color: #c0c0be;
  border-color: #484845;
}

.analytics-date-select:focus {
  border-color: #484845;
}

.analytics-date-select option {
  background: #141412;
  color: #909090;
}
</style>
